/**
 * formatWhatsAppText
 * Parsea el formato de texto de WhatsApp y devuelve un array de segmentos
 * para renderizar con React (evita dangerouslySetInnerHTML).
 *
 * Formatos WhatsApp:
 * - *texto* → negrita
 * - _texto_ → cursiva
 * - ~texto~ → tachado
 * - ```texto``` → monospace
 *
 * @param {string} text - Texto plano con marcadores WhatsApp
 * @returns {Array<{type: 'bold'|'italic'|'strikethrough'|'monospace'|'plain', content: string}>}
 */
export function formatWhatsAppText(text) {
  if (!text || typeof text !== "string") return [{ type: "plain", content: "" }];

  const segments = [];
  let remaining = text;

  // Regex para cada formato (en orden de prioridad: monospace primero por ser 3 chars)
  // Usamos patrones que evitan cruces: *bold*, _italic_, ~strike~, ```mono```
  const patterns = [
    {
      type: "monospace",
      regex: /```([^`]+)```/g,
      priority: 1,
    },
    {
      type: "bold",
      regex: /\*([^*]+)\*/g,
      priority: 2,
    },
    {
      type: "italic",
      regex: /_([^_]+)_/g,
      priority: 3,
    },
    {
      type: "strikethrough",
      regex: /~([^~]+)~/g,
      priority: 4,
    },
  ];

  // Algoritmo: encontrar la primera coincidencia de cualquier patrón
  const findNextMatch = (str) => {
    let earliest = null;
    let earliestType = null;
    let earliestRegex = null;

    for (const { type, regex } of patterns) {
      regex.lastIndex = 0;
      const match = regex.exec(str);
      if (match && (earliest === null || match.index < earliest.index)) {
        earliest = match;
        earliestType = type;
        earliestRegex = regex;
      }
    }

    return earliest ? { match: earliest, type: earliestType, regex: earliestRegex } : null;
  };

  while (remaining.length > 0) {
    const next = findNextMatch(remaining);

    if (!next) {
      segments.push({ type: "plain", content: remaining });
      break;
    }

    const { match, type } = next;
    const before = remaining.slice(0, match.index);
    const content = match[1];
    const fullMatch = match[0];
    const afterStart = match.index + fullMatch.length;

    if (before.length > 0) {
      segments.push({ type: "plain", content: before });
    }
    segments.push({ type, content });
    remaining = remaining.slice(afterStart);
  }

  return segments;
}

/**
 * Convierte segmentos a elementos React (para uso con map)
 */
export function segmentsToReactFormat(segments) {
  return segments.map((seg, i) => ({
    ...seg,
    key: `seg-${i}-${seg.type}`,
  }));
}
